import { useCallback, useEffect, useRef, useState } from 'react';
import { useKeycloak } from '../components/KeycloakProvider';
import { useApiClient } from './useApiClient';
import { User } from '../types';
import { Project } from '../types/reports';

/**
 * Base response interface for filter options endpoints
 */
export interface BaseFilterOptionsResponse {
  availableUsers: Array<{ id: string; displayName: string }>;
  availableProjects: Array<{ id: string; key: string; name: string }>;
  isAdmin: boolean;
}

/**
 * Extended response for dashboard that includes statuses
 */
export interface DashboardFilterOptionsResponse extends BaseFilterOptionsResponse {
  availableStatuses: string[];
  availableLabels: string[];
}

export interface FilterOptionsResult<T extends BaseFilterOptionsResponse> {
  availableUsers: User[];
  availableProjects: Project[];
  isAdmin: boolean;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  rawResponse: T | null;
}

/**
 * Generic hook for fetching filter options from various endpoints.
 * Handles common logic: loading state, error handling, StrictMode protection, and AbortController.
 *
 * @param endpoint - API endpoint to fetch filter options from
 * @returns Filter options data and state
 */
export const useFilterOptionsBase = <T extends BaseFilterOptionsResponse>(
  endpoint: string
): FilterOptionsResult<T> => {
  const { keycloak, initialized } = useKeycloak();
  const apiClient = useApiClient();

  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [availableProjects, setAvailableProjects] = useState<Project[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rawResponse, setRawResponse] = useState<T | null>(null);

  // Prevent double fetch in StrictMode
  const fetchedRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchFilterOptions = useCallback(async (force = false) => {
    if (!initialized || !keycloak?.authenticated) {
      setLoading(false);
      return;
    }

    // Skip if already fetched (unless forced)
    if (fetchedRef.current && !force) {
      return;
    }
    fetchedRef.current = true;

    // Abort previous request if forced refresh
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      setLoading(true);
      const response = await apiClient.get<T>(endpoint, { signal: abortController.signal });

      setRawResponse(response);

      setAvailableUsers(
        response.availableUsers.map((u) => ({
          id: u.id,
          displayName: u.displayName,
          name: u.displayName,
          accountId: u.id,
          createTime: '',
          updateTime: '',
        }))
      );

      setAvailableProjects(
        response.availableProjects.map((p) => ({
          id: p.id,
          key: p.key,
          name: p.name,
          displayName: p.name,
        }))
      );

      setIsAdmin(response.isAdmin);
      setError(null);
    } catch (err) {
      // Ignore abort errors
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }
      setError('Failed to load filter options');
      console.error(`Failed to fetch filter options from ${endpoint}:`, err);
      // Reset flag on error so it can retry
      fetchedRef.current = false;
    } finally {
      if (abortControllerRef.current === abortController) {
        setLoading(false);
      }
    }
  }, [apiClient, endpoint, initialized, keycloak?.authenticated]);

  // Fetch ONCE on mount
  useEffect(() => {
    fetchFilterOptions();
    return () => {
      // Reset fetchedRef on cleanup so StrictMode re-mount will fetch again
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        fetchedRef.current = false;
      }
    };
  }, [fetchFilterOptions]);

  return {
    availableUsers,
    availableProjects,
    isAdmin,
    loading,
    error,
    refresh: () => fetchFilterOptions(true),
    rawResponse,
  };
};
